import { useState, useEffect } from 'react';
import {
  Brain,
  Search,
  RefreshCw,
  Calendar,
  TrendingUp,
  Lightbulb,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import { Button, Card, Badge, Input, LoadingSpinner, Alert } from '@/components/ui';
import { api } from '@/lib/api';
import type { ResearchHistoryEntry, ResearchStats, SimilarResearchResponse } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

export function ResearchHistoryPage() {
  const [entries, setEntries] = useState<ResearchHistoryEntry[]>([]);
  const [stats, setStats] = useState<ResearchStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SimilarResearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [historyRes, statsRes] = await Promise.all([
        api.getResearchHistory(20, 0),
        api.getResearchStats(),
      ]);
      setEntries(historyRes.entries);
      setStats(statsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load research history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) return;
    setIsSearching(true);
    setError(null);
    try {
      const results = await api.findSimilarResearch(searchQuery, 10, 0.3);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const displayEntries = searchResults ? searchResults.results.map(r => r.entry) : entries;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            Research History
          </h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">
            Your AI-powered research insights and knowledge base
          </p>
        </div>
        <Button variant="secondary" onClick={fetchData} leftIcon={<RefreshCw className="w-4 h-4" />} className="self-start sm:self-auto">
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="text-center p-3 sm:p-4">
            <div className="text-2xl sm:text-3xl font-bold text-primary">{stats.total_entries}</div>
            <div className="text-text-secondary text-xs sm:text-sm">Total Research</div>
          </Card>
          <Card className="text-center p-3 sm:p-4">
            <div className="text-2xl sm:text-3xl font-bold text-green-500">
              {(stats.avg_confidence * 100).toFixed(0)}%
            </div>
            <div className="text-text-secondary text-xs sm:text-sm">Avg Confidence</div>
          </Card>
          <Card className="text-center p-3 sm:p-4">
            <div className="text-2xl sm:text-3xl font-bold text-blue-500">
              {stats.avg_tasks_per_research.toFixed(1)}
            </div>
            <div className="text-text-secondary text-xs sm:text-sm">Avg Tasks/Research</div>
          </Card>
          <Card className="text-center p-3 sm:p-4">
            <div className="text-2xl sm:text-3xl font-bold text-purple-500">{stats.total_research_days}</div>
            <div className="text-text-secondary text-xs sm:text-sm">Days Active</div>
          </Card>
        </div>
      )}

      {/* Semantic Search */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Search your research history..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            leftIcon={<Search className="w-5 h-5" />}
            className="flex-1"
          />
          <div className="flex gap-2 sm:gap-3">
            <Button onClick={handleSearch} isLoading={isSearching} disabled={searchQuery.length < 3} className="flex-1 sm:flex-none">
              <Sparkles className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Find Similar</span>
              <span className="sm:hidden">Search</span>
            </Button>
            {searchResults && (
              <Button variant="secondary" onClick={clearSearch}>
                Clear
              </Button>
            )}
          </div>
        </div>
        {searchResults && (
          <p className="text-text-secondary text-sm mt-2">
            Found {searchResults.count} similar research entries for "{searchResults.query}"
          </p>
        )}
      </Card>

      {/* Research Entries */}
      {displayEntries.length === 0 ? (
        <Card className="text-center py-12">
          <Brain className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No research history yet</h3>
          <p className="text-text-secondary">
            Your research insights will appear here after generating newsletters.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {displayEntries.map((entry) => {
            const isExpanded = expandedEntries.has(entry.id);
            const similarityScore = searchResults?.results.find(r => r.entry.id === entry.id)?.similarity;

            return (
              <Card key={entry.id} className="overflow-hidden">
                <div
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => toggleExpanded(entry.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{entry.topic}</h3>
                      {similarityScore && (
                        <Badge variant="primary">
                          {(similarityScore * 100).toFixed(0)}% match
                        </Badge>
                      )}
                      <Badge variant={entry.confidence_score > 0.7 ? 'success' : 'warning'}>
                        {(entry.confidence_score * 100).toFixed(0)}% confidence
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-text-secondary text-sm">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(entry.research_date), 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        {entry.task_count} tasks
                      </span>
                      <span className="text-text-muted">
                        {formatDistanceToNow(new Date(entry.research_date), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-surface-light rounded-lg transition-colors">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-text-secondary" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-text-secondary" />
                    )}
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border space-y-4">
                    {/* Key Insights */}
                    {entry.key_insights.length > 0 && (
                      <div>
                        <h4 className="font-medium flex items-center gap-2 mb-2">
                          <Lightbulb className="w-4 h-4 text-yellow-500" />
                          Key Insights
                        </h4>
                        <ul className="space-y-1 text-text-secondary">
                          {entry.key_insights.slice(0, 5).map((insight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>{insight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Entities */}
                    {Object.keys(entry.entities).length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Entities & Topics</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(entry.entities).map(([category, items]) =>
                            (items as string[]).slice(0, 5).map((item, idx) => (
                              <Badge key={`${category}-${idx}`} variant="default">
                                {item}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sources */}
                    {entry.sources_used.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Sources Used ({entry.sources_used.length})</h4>
                        <div className="space-y-1">
                          {entry.sources_used.slice(0, 5).map((source, idx) => (
                            <a
                              key={idx}
                              href={source}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary hover:underline truncate"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{source}</span>
                            </a>
                          ))}
                          {entry.sources_used.length > 5 && (
                            <p className="text-text-muted text-sm">
                              +{entry.sources_used.length - 5} more sources
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

